/**
 * Purpose: Game configuration screen — choose pack, win condition, schedule mode, and start.
 * Owns: Pack selection, game mode selector (Speed Run / Honor Roll),
 *       schedule mode selector (Random / Choose), schedule picker integration,
 *       solo mode badge, game-start validation, start button.
 * Notes: For Phase 1, only the "Family Road Trip" pack is available.
 *        Supports 1-8 players (solo play is valid). When exactly 1 player,
 *        descriptions adjust for solo context and a "Solo Mode" badge shows.
 *        Player count limit imported from classConfig (CS-21: config-driven).
 */

import type { GameMode, ScheduleMode } from '../../types/game';
import type { Category } from '../../types/question';
import { MIN_PLAYERS } from '../../config/classConfig';
import { SchedulePicker } from './SchedulePicker';

interface GameSetupProps {
  /** Number of players currently added */
  playerCount: number;
  /** Selected question pack ID */
  selectedPackId: string;
  /** Current win condition mode */
  gameMode: GameMode;
  /** Called when win condition mode changes */
  onGameModeChange: (mode: GameMode) => void;
  /** Current schedule mode */
  scheduleMode: ScheduleMode;
  /** Called when schedule mode changes */
  onScheduleModeChange: (mode: ScheduleMode) => void;
  /** Current custom subject order (used when scheduleMode is 'choose') */
  customSubjectOrder: Category[];
  /** Called when the custom subject order changes */
  onSubjectOrderChange: (subjects: Category[]) => void;
  /** Called when the host taps "Start Game" */
  onStartGame: () => void;
}

/** Available question packs (CS-21: config-driven, will grow over time). */
const AVAILABLE_PACKS = [
  {
    id: 'family-road-trip',
    name: 'Family Road Trip',
    description: 'The classic family trivia pack for all ages!',
  },
];

/** Win condition options with multi-player and solo descriptions (CS-21). */
const GAME_MODE_OPTIONS: {
  value: GameMode;
  label: string;
  description: string;
  soloDescription: string;
}[] = [
  {
    value: 'speedRun',
    label: 'Speed Run',
    description: 'First to finish all classes wins!',
    soloDescription: 'Complete all classes and the Final Exam!',
  },
  {
    value: 'honorRoll',
    label: 'Honor Roll',
    description: 'Everyone plays \u2014 highest GPA wins!',
    soloDescription: 'Can you beat a 3.0 GPA?',
  },
  {
    value: 'pureClassroom',
    label: 'Pure Classroom',
    description: 'No dice, no movement \u2014 just answer, get graded, earn your GPA!',
    soloDescription: 'Answer questions, get graded, earn your GPA!',
  },
];

/** Schedule mode options with descriptions. */
const SCHEDULE_MODE_OPTIONS: {
  value: ScheduleMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'random',
    label: 'Random',
    description: 'Subjects are shuffled each game',
  },
  {
    value: 'choose',
    label: 'Pick Your Schedule',
    description: 'Choose your class order',
  },
];

export function GameSetup({
  playerCount,
  selectedPackId,
  gameMode,
  onGameModeChange,
  scheduleMode,
  onScheduleModeChange,
  customSubjectOrder,
  onSubjectOrderChange,
  onStartGame,
}: GameSetupProps) {
  const canStart = playerCount >= MIN_PLAYERS;
  const isSolo = playerCount === 1;

  return (
    <div style={styles.container}>
      {/* Solo mode badge — shown only when exactly 1 player */}
      {isSolo && (
        <div style={styles.soloBadge}>
          <span style={styles.soloBadgeText}>Solo Mode</span>
        </div>
      )}

      {/* Win condition selector */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Win Condition</h3>
        <div style={styles.optionList}>
          {GAME_MODE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              style={{
                ...styles.optionCard,
                ...(gameMode === opt.value ? styles.optionCardActive : {}),
              }}
              onClick={() => onGameModeChange(opt.value)}
              aria-pressed={gameMode === opt.value}
            >
              <span style={styles.optionLabel}>{opt.label}</span>
              <span style={styles.optionDesc}>
                {isSolo ? opt.soloDescription : opt.description}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Schedule mode selector */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Schedule</h3>
        <div style={styles.optionList}>
          {SCHEDULE_MODE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              style={{
                ...styles.optionCard,
                ...(scheduleMode === opt.value ? styles.optionCardActive : {}),
              }}
              onClick={() => onScheduleModeChange(opt.value)}
              aria-pressed={scheduleMode === opt.value}
            >
              <span style={styles.optionLabel}>{opt.label}</span>
              <span style={styles.optionDesc}>{opt.description}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Schedule picker — only when "Pick Your Schedule" is selected */}
      {scheduleMode === 'choose' && (
        <SchedulePicker
          subjects={customSubjectOrder}
          onReorder={onSubjectOrderChange}
        />
      )}

      {/* Pack selection */}
      <div style={styles.section}>
        <h3 style={styles.sectionTitle}>Question Pack</h3>
        <div style={styles.optionList}>
          {AVAILABLE_PACKS.map((pack) => (
            <div
              key={pack.id}
              style={{
                ...styles.optionCard,
                ...(selectedPackId === pack.id ? styles.optionCardActive : {}),
              }}
            >
              <span style={styles.optionLabel}>{pack.name}</span>
              <span style={styles.optionDesc}>{pack.description}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Start game */}
      <button
        style={{
          ...styles.startButton,
          opacity: canStart ? 1 : 0.5,
        }}
        onClick={onStartGame}
        disabled={!canStart}
        aria-label="Start the game"
      >
        {canStart
          ? 'Start Game!'
          : `Need at least ${MIN_PLAYERS} player`}
      </button>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
    padding: '1rem',
    width: '100%',
    maxWidth: '480px',
    margin: '0 auto',
  },
  soloBadge: {
    alignSelf: 'center',
    padding: '0.35rem 1rem',
    borderRadius: '20px',
    backgroundColor: 'var(--accent-gold)',
    boxShadow: '0 2px 0 rgba(0,0,0,0.2)',
  },
  soloBadgeText: {
    fontSize: '0.85rem',
    fontWeight: '700',
    color: 'var(--text-dark)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  sectionTitle: {
    fontSize: '1rem',
    fontWeight: '700',
    color: 'var(--text-light)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    margin: 0,
  },
  optionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  optionCard: {
    padding: '1rem',
    borderRadius: '10px',
    backgroundColor: 'var(--bg-surface)',
    border: '2px solid transparent',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    cursor: 'pointer',
    textAlign: 'left' as const,
  },
  optionCardActive: {
    border: '2px solid var(--accent-gold)',
  },
  optionLabel: {
    fontSize: '1.1rem',
    fontWeight: '700',
    color: 'var(--text-light)',
  },
  optionDesc: {
    fontSize: '0.85rem',
    color: 'var(--muted)',
  },
  startButton: {
    padding: '1rem',
    borderRadius: '12px',
    backgroundColor: 'var(--accent-gold)',
    color: 'var(--text-dark)',
    fontSize: '1.2rem',
    fontWeight: '700',
    border: 'none',
    cursor: 'pointer',
    boxShadow: '0 4px 0 rgba(0,0,0,0.2)',
  },
};
